import {
  createBrowserRouter,
  Navigate,
  Outlet,
  redirect,
  RouteObject,
  useLocation,
} from "react-router-dom";
import { UserGuides } from "./components/pages/UserGuides";
import { Snackbar } from "components/organisms/Snackbar";
import { Customers } from "components/pages/Customers";
import { ErrorNotFound } from "components/pages/ErrorNotFound";
import { Dashboard } from "components/pages/Dashboard";
import { Profile } from "components/pages/Profile";
import { SideNavWithContent } from "components/templates/SideNavWithContent";
import { Appointments } from "components/pages/Appointments";
import { Support } from "components/pages/Support";
import { Authentication } from "components/organisms/Authentication";
import { PageWrapper } from "components/organisms/PageWrapper";
import { ViewCustomer } from "components/pages/ViewCustomer";
import { CreateCustomer } from "components/pages/CreateCustomer";
import { Masquerade } from "components/pages/Masquerade";
import { ScheduleAppointment } from "components/pages/ScheduleAppointment";
import { NotificationsList } from "components/pages/NotificationsList";
import { ChannelSelectionDirector } from "components/organisms/ChannelSelectionDirector";
import { getNewPath } from "adapters/common";
import { CustomerProfile } from "components/pages/CustomerProfile";
import { OneAppointment } from "components/pages/OneAppointment";
import { Metrics } from "components/pages/Metrics";
import { Analytics } from "components/pages/Analytics";
import { AcademyRoute } from "components/pages/AcademyRoute";
import { Pipeline } from "components/pages/Pipeline";
import { Chat } from "components/pages/Chat";
import { SplatLogin } from "components/pages/SplatLogin";
import { useAuthAdapter } from "adapters/auth";
import { RetailAdmin } from "components/pages/RetailAdmin";
import { Megan } from "components/pages/Megan";
import { RunxRadarRedirect } from "components/pages/RunxRadarRedirect";
import { Performance } from "components/pages/Performance";
import { RetailRecordingGate } from "features/retailRecording/components/RetailRecordingGate";

type Path =
  | "/"
  | "appointment"
  | "/appointments"
  | "/dashboard"
  | "/pipeline"
  | "/analytics"
  | "/performance"
  | "/customers"
  | "/customers/create"
  | "/customers/customer-profile"
  | "/map"
  | "/metrics"
  | "/notifications"
  | "/performance"
  | "/masquerade"
  | "/profile"
  | "/academy"
  | "/support"
  | "/support/playbook"
  | "/user-guides"
  | "/messages"
  | "/megan"
  | "/sales-support"
  | "/run-x-radar-redirect"
  | "*";

type DeepPath = Path | `${Path}/*`;

const Redirect = (props: { to: string }) => {
  const path = getNewPath(props.to);
  return <Navigate to={path} replace />;
};

const AppLayout = () => {
  const { pathname } = useLocation();
  const authProps = useAuthAdapter(
    pathname === "/splat-login" || pathname === "/force-web-splat-login"
      ? "splatLogin"
      : "login"
  );
  // @ts-ignore window.ReactNativeWebView is only available in the mobile app
  const isInOneMobileApp = window.ReactNativeWebView;

  const hideNav =
    pathname === "/customers/create" || authProps.showAuth || isInOneMobileApp;

  return (
    <SideNavWithContent hideNav={hideNav}>
      <PageWrapper hideNav={hideNav}>
        <Authentication
          basePath={authProps.basePath}
          src={authProps.src}
          title={authProps.title}
          ref={authProps.ref}
          showAuth={authProps.showAuth}
          isLoaded={authProps.isLoaded}
        >
          <Outlet />
          <ChannelSelectionDirector />
          <Snackbar />
          <RetailRecordingGate />
        </Authentication>
      </PageWrapper>
    </SideNavWithContent>
  );
};

const routes: Array<RouteObject & { path: DeepPath }> = [
  {
    path: "/",
    element: <AppLayout />,
    children: [
      {
        path: "appointments/details/:id",
        loader: ({ params }) => {
          return redirect(`/customers/details/${params.id}`);
        },
      },
      {
        path: "appointments/prospects/:id",
        loader: ({ params }) => {
          return redirect(`/customers/prospects/${params.id}`);
        },
      },
      {
        path: "appointments/customer-profile/:id",
        loader: ({ params }) => {
          return redirect(
            `/customers/customer-profile/${params.id}?initialTab=appointments`
          );
        },
      },
      {
        path: "pipeline/customer-profile/:id",
        loader: ({ params }) => {
          return redirect(`/customers/customer-profile/${params.id}`);
        },
      },
      {
        path: "pipeline/prospects/:id",
        loader: ({ params }) => {
          return redirect(`/customers/prospects/${params.id}`);
        },
      },
      {
        path: "appointments",
        element: <Appointments />,
      },
      {
        path: "messages",
        element: <Chat />,
      },
      {
        path: "megan/*",
        element: <Megan />,
      },
      {
        path: "sales-support/*",
        element: <Megan />,
      },
      {
        path: "run-x-radar-redirect",
        element: <RunxRadarRedirect />,
      },
      {
        path: "customers/*",
        element: <Customers />,
      },
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "performance/*",
        element: <Performance />,
      },
      {
        path: "pipeline",
        element: <Pipeline />,
      },
      {
        path: "analytics",
        element: <Analytics />,
      },
      {
        path: "masquerade",
        element: <Masquerade />,
      },
      {
        path: "metrics",
        element: <Metrics />,
      },
      {
        path: "notifications",
        element: <NotificationsList />,
      },
      {
        path: "profile",
        element: <Profile />,
      },
      {
        path: "academy",
        element: <AcademyRoute />,
      },
      {
        path: "support/*",
        element: <Support />,
      },
      {
        path: "customers/create",
        element: <CreateCustomer />,
      },
      {
        path: "customers/prospects/:id",
        element: <ViewCustomer />,
      },
      {
        path: "customers/customer-profile/:id/*",
        element: <CustomerProfile />,
      },
      {
        path: "appointment/*",
        element: <OneAppointment />,
      },
      {
        path: "schedule-appointment/:id",
        element: <ScheduleAppointment />,
      },
      {
        path: "/splat-login",
        element: <SplatLogin />,
      },
      {
        path: "/force-web-splat-login", // Alias for /splat-login: used to prevent mobile users from being automatically redirected into the app
        element: <SplatLogin />,
      },
      {
        path: "/user-guides",
        element: <UserGuides />,
      },
      {
        path: "/retail-admin",
        element: <RetailAdmin />,
      },
      {
        path: "/",
        element: <Redirect to="/dashboard" />,
      },
      {
        path: "*",
        element: <ErrorNotFound />,
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export { router };
export type { Path };
